import { QueryClient } from "@tanstack/react-query";

import { LeadEntityType as EntityType } from "../../../../types/graphQLEnums";
import { ContactResult } from "../../contacts/useContacts";
import { updateCachedContacts } from "../../contacts/utils";
import { DocumentDetails } from "../../documents/useDocument";
import { updateCachedDocuments } from "../../documents/utils";
import { NoticeDetails } from "../../notices/useNotice";
import { NoticesDto } from "../../notices/useNotices";
import { updateCachedNotices } from "../../notices/utils";

/**
 * On a given entity, either removes or adds the lead ID and name from it's attached leads object
 * @param param0
 */
export function updateLeadEntityQuery({
  queryClient,
  entityId,
  entityType,
  leadName,
  leadId,
  isAdding,
}: {
  queryClient: QueryClient;
  entityId: string;
  entityType: EntityType;
  leadName: string;
  leadId: string;
  isAdding: boolean;
}) {
  // These entity types have no per-entity query that carries a `leads` ResolveField
  // to patch here.
  if (
    [
      EntityType.Buyer,
      EntityType.Supplier,
      EntityType.Organisation,
      EntityType.CustomOrganisation,
      EntityType.BuyerActivityReport,
      EntityType.Framework,
      EntityType.ContactCognism,
    ].includes(entityType)
  ) {
    return;
  }

  switch (entityType) {
    case EntityType.Contact:
      updateCachedContacts({
        queryClient,
        contactId: entityId,
        updateContactsRow: (contact) =>
          updateContactsLeads({
            contact,
            leadId,
            leadName,
            isAdding,
          }),
      });

      break;

    case EntityType.Notice: {
      updateCachedNotices({
        queryClient,
        lookup: { type: "procurementStageId", id: entityId },
        updateNoticesRow: (notice) => updateNoticeLeads({ leadId, leadName, isAdding, notice }),
        updateNoticeQuery: (notice) => updateNoticeLeads({ leadId, leadName, isAdding, notice }),
      });
      break;
    }

    case EntityType.Document:
      updateCachedDocuments({
        queryClient,
        documentId: entityId,
        updateDocumentsRow: (document) =>
          updateDocumentLeads({
            document,
            leadId,
            leadName,
            isAdding,
          }),
        updateDocumentQuery: (document) =>
          updateDocumentLeads({
            document,
            leadId,
            leadName,
            isAdding,
          }),
      });

      break;

    default:
      throw new Error(`Unhandled lead entity type: ${entityType}`);
  }
}

type LeadRef = { id: string; name: string };

/**
 * Reusable function which updates the leads field on an entity by either adding or removing a lead
 * @param param0
 * @returns
 */
function updateEntityLeads<TEntity extends { leads: ReadonlyArray<LeadRef> }>({
  entity,
  leadId,
  leadName,
  isAdding,
}: {
  entity: TEntity;
  leadId: string;
  leadName: string;
  isAdding: boolean;
}): TEntity {
  const leads = entity.leads ?? [];

  // --- Removing a lead ---
  if (!isAdding) {
    // Find the index once; avoids creating a new array if the lead isn't present.
    const idx = leads.findIndex((l) => l.id === leadId);

    // If it's not there, nothing to do — return same reference for performance.
    if (idx === -1) return entity;

    // Remove exactly one matching lead by id.
    // (If duplicates somehow exist, this removes the first; adjust if you want "remove all".)
    const nextLeads = leads.slice(0, idx).concat(leads.slice(idx + 1));

    // Return a new object with updated leads.
    return {
      ...entity,
      leads: nextLeads,
    };
  }

  // --- Adding a lead ---
  // Only add if it doesn't already exist.
  const exists = leads.some((l) => l.id === leadId);

  // If it's already present, nothing to do — return same reference for performance.
  if (exists) return entity;

  // Add the lead.
  const nextLeads = [{ id: leadId, name: leadName }, ...leads];

  return {
    ...entity,
    leads: nextLeads,
  };
}

/**
 * Updates a contact's leads field in the query cache
 */
function updateContactsLeads({
  contact,
  leadId,
  leadName,
  isAdding,
}: {
  contact: ContactResult;
  leadId: string;
  leadName: string;
  isAdding: boolean;
}): ContactResult {
  return updateEntityLeads({
    entity: contact,
    leadId,
    leadName,
    isAdding,
  });
}

/**
 * Updates the leads field for a notice object in the query cache
 */
function updateNoticeLeads<NoticeType extends NoticesDto | NoticeDetails>({
  notice,
  leadId,
  leadName,
  isAdding,
}: {
  notice: NoticeType;
  leadId: string;
  leadName: string;
  isAdding: boolean;
}): NoticeType {
  return updateEntityLeads({
    entity: notice,
    leadId,
    leadName,
    isAdding,
  });
}

/**
 * Updates the useDocument query cache's leads field for a given document ID
 * that corresponds to entityId
 */
function updateDocumentLeads({
  document,
  leadId,
  leadName,
  isAdding,
}: {
  document: DocumentDetails;
  leadId: string;
  leadName: string;
  isAdding: boolean;
}) {
  return updateEntityLeads({
    entity: document,
    leadId,
    leadName,
    isAdding,
  });
}
